/* eslint-disable @typescript-eslint/naming-convention */
// Allowed list of platform options accessible at runtime, with derived type for buildtime checks
import { categories } from './ccm';

export type ToolCategory = 'SIEM' | 'EDR' | 'NDR' | 'XDR' | 'Misc.';

export const supportedTools = [
  'CarbonBlack Cloud - LiveQuery',
  'CarbonBlack Cloud - Investigate',
  'CarbonBlack Response',
  'Corelight Investigator',
  'CrowdStrike',
  'CrowdStrike LogScale',
  'Elastic Signal',
  'Elastic',
  'Google SecOps',
  'Microsoft Defender',
  'Microsoft Sentinel',
  'Palo Alto Cortex XDR',
  'QRadar Query',
  'SentinelOne',
  'SentinelOne - PowerQuery',
  'SentinelOne Singularity',
  'Snort',
  'Splunk',
  'Suricata',
  'Trend Micro Vision One',
  'Tanium',
  'Tanium Signal',
  'Yara',
] as const;
export type SupportedTools = (typeof supportedTools)[number];

type categoryKeys = keyof typeof categories;

export interface MappingSupport {
  name: SupportedTools;
  category: ToolCategory;
  customMapping: boolean;
  toolSpecific?: string[];
  allowedCategories?: categoryKeys[];
  // can't figure out how to enforce json category key type
}

export const SupportedMappings: Array<MappingSupport> = [
  { name: 'Elastic', category: 'SIEM', customMapping: true },
  {
    name: 'Elastic Signal',
    category: 'SIEM',
    customMapping: true,
  },
  {
    name: 'Microsoft Sentinel',
    category: 'SIEM',
    customMapping: true,
    allowedCategories: ['endpoint:wineventlog'],
    toolSpecific: ['tableName'],
  },
  {
    name: 'Splunk',
    category: 'SIEM',
    customMapping: true,
    toolSpecific: ['index', 'sourceType'],
  },
  {
    name: 'QRadar Query',
    category: 'SIEM',
    customMapping: true,
  },
  {
    name: 'CarbonBlack Cloud - Investigate',
    category: 'EDR',
    customMapping: false,
  },
  {
    name: 'CarbonBlack Cloud - LiveQuery',
    category: 'EDR',
    customMapping: false,
  },
  {
    name: 'CarbonBlack Response',
    category: 'EDR',
    customMapping: false,
  },
  {
    name: 'SentinelOne - PowerQuery',
    category: 'EDR',
    customMapping: false,
  },
  { name: 'Snort', category: 'Misc.', customMapping: false },
  { name: 'Yara', category: 'Misc.', customMapping: false },
  { name: 'CrowdStrike', category: 'EDR', customMapping: false },
  { name: 'CrowdStrike LogScale', category: 'EDR', customMapping: false },
  { name: 'Google SecOps', category: 'SIEM', customMapping: false },
  {
    name: 'Microsoft Defender',
    category: 'EDR',
    customMapping: false,
  },
  {
    name: 'SentinelOne',
    category: 'EDR',
    customMapping: false,
  },
  {
    name: 'SentinelOne Singularity',
    category: 'XDR',
    customMapping: false,
  },
  {
    name: 'Palo Alto Cortex XDR',
    category: 'XDR',
    customMapping: false,
  },
  {
    name: 'Trend Micro Vision One',
    category: 'XDR',
    customMapping: false,
  },
  {
    name: 'Tanium',
    category: 'EDR',
    customMapping: false,
  },
  {
    name: 'Tanium Signal',
    category: 'EDR',
    customMapping: false,
  },
  {
    name: 'Corelight Investigator',
    category: 'NDR',
    customMapping: false,
  },
];

export const SupportedToolMappings = {
  Elastic: { category: 'SIEM', customMapping: true },
  'Elastic Signal': { category: 'SIEM', customMapping: true },
  'Microsoft Sentinel': {
    category: 'SIEM',
    customMapping: true,
  },
  Splunk: { category: 'SIEM;', customMapping: true },
  'QRadar Query': { category: 'SIEM', customMapping: true },
  'CarbonBlack Cloud - Investigate': {
    category: 'EDR',
    customMapping: false,
  },
  'CarbonBlack Cloud - LiveQuery': {
    category: 'EDR',
    customMapping: false,
  },
  'CarbonBlack Response': {
    category: 'EDR',
    customMapping: false,
  },
  Snort: { category: 'Misc.', customMapping: false },
  Yara: { category: 'Misc.', customMapping: false },
  CrowdStrike: { category: 'EDR', customMapping: false },
  'CrowdStrike LogScale': { category: 'EDR', customMapping: false },
  'Google SecOps': { category: 'SIEM', customMapping: false },
  'Microsoft Defender': {
    category: 'EDR',
    customMapping: false,
  },
  SentinelOne: { category: 'EDR', customMapping: false },
  'SentinelOne PowerQuery': { category: 'EDR', customMapping: false },
  'SentinelOne Singularity': { category: 'XDR', customMapping: false },
  'Palo Alto Cortex XDR': {
    category: 'XDR',
    customMapping: false,
  },
  Tanium: {
    category: 'EDR',
    customMapping: false,
  },
  'Tanium Signal': {
    category: 'EDR',
    customMapping: false,
  },
  'Trend Micro Vision One': {
    category: 'XDR',
    customMapping: false,
  },
  'Corelight Investigator': {
    category: 'NDR',
    customMapping: false,
  },
};

/**
const currentlySupportedTools: Array<SupportedTools> = [
  'CarbonBlack',
  'CarbonBlack Response',
  'CrowdStrike',
  'Elastic',
  'Elastic Signal',
  'Snort',
  'Splunk',
  'Yara',
] as const;
 */
export class MatchboiError extends Error {}

export interface CustomerDataParsed {
  index: string;
  sourcetype?: string;
  fields: Array<string>;
}

export type CustomerMapParsed = Array<CustomerDataParsed>;

export interface EnvironmentTool {
  id: string;
  created: Date;
  lastModified: Date;
  name: string;
  product: SupportedTools;
  rootUrl: string;
  environment: Environment;
  fieldMappings?: FieldMapping[];
}

export interface FieldMapping {
  name: string;
  category: string;
  fields?: {
    [ccmField: string]: string;
  }[];
  toolSpecific?: {
    [ccmField: string]: string;
  }[];
}

export interface Environment {
  id: string;
  created: Date;
  lastModified: Date;
  name: string;
  description: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  company: any;
  tools: EnvironmentTool[];
}
